import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import { useNavigate, Link, Outlet } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import AccountDashboardNavbar from "../ps-account/AccountDashboardNavbar";
import {
  FaBriefcase,
  FaDollarSign,
  FaSearch,
  FaUserCog,
  FaRegHourglass,
  FaRegListAlt,
  FaTimes,
} from "react-icons/fa";
import { TiMessages } from "react-icons/ti";
import { MdDashboard } from "react-icons/md";
import { MdCalendarToday } from "react-icons/md";
import useAutoLogout from "../../util_functions/UseAutoLogout";
import config from "../../config/config";
import { useUser } from "../../context/UserContext";
import useOutsideClick from "../../util_functions/UseOutsideClick";
import WarningModal from "../../util_functions/WarningModal";

const Dashboard = () => {
  const {
    loggedInUser,
    setLoggedInUser,
    fetchLoggedInUser,
    clientUser,
    setClientUser,
    fetchUserDetailsById,
    logout,
  } = useUser();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const dashboardMainContainerRef = useRef(null);
  const dashboardContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  const navbarRef = useRef(null);
  const notificationIconRef = useRef(null);
  const accountIconRef = useRef(null);

  const [showNotificationPane, setShowNotificationPane] = useState({
    show: false,
    top: 0,
    left: 0,
    bottom: null,
    iconWidth: 0,
  });

  const [showAccountPane, setShowAccountPane] = useState({
    show: false,
    top: 0,
    left: 0,
    right: 0,
  });

  const notificationPaneRef = useOutsideClick(() => {
    setShowNotificationPane((prev) => ({ ...prev, show: false }));
  }, [notificationIconRef]);

  const accountPaneRef = useOutsideClick(() => {
    setShowAccountPane((prev) => ({ ...prev, show: false }));
  }, [accountIconRef]);

  const navigate = useNavigate();
  const { resetTimer, showWarning, remainingTime } = useAutoLogout(logout);

  const imgUrl = config.PROFILE_IMAGES_URL;
  const avatarDefaultUrl = config.AVATAR_DEFAULT_URL;

  useEffect(() => {
    const adjustLayout = () => {
      if (
        navbarRef.current &&
        dashboardContainerRef.current &&
        dashboardMainContainerRef.current &&
        sidebarRef.current
      ) {
        const navbarHeight = navbarRef.current.offsetHeight;
        setNavbarHeight(navbarHeight);

        sidebarRef.current.style.top = `${navbarHeight}px`;
        sidebarRef.current.style.height = `calc(100% - ${navbarHeight}px)`;

        const dashboardContainerHeight =
          dashboardContainerRef.current.offsetHeight;
        const dashboardMainContainerHeight =
          dashboardMainContainerRef.current.offsetHeight;

        if (isMobile) {
          const marginTopValue = -(
            dashboardMainContainerHeight -
            (dashboardContainerHeight + navbarHeight)
          );
          console.log("Margin Top Value:", marginTopValue);
          dashboardContainerRef.current.style.marginTop = `${marginTopValue}px`;
        } else {
          dashboardContainerRef.current.style.marginTop = "0px";
          dashboardContainerRef.current.style.marginBottom = "0px";
        }

        console.log("Is Mobile:", isMobile);
        console.log("Navbar height:", navbarHeight);
        console.log("Dashboard container height:", dashboardContainerHeight);
        console.log(
          "Dashboard main container height:",
          dashboardMainContainerHeight
        );
      }
    };

    adjustLayout();
    window.addEventListener("resize", adjustLayout);
    return () => window.removeEventListener("resize", adjustLayout);
  }, [isMobile, navbarHeight]);

  useEffect(() => {
    if (!loggedInUser) {
      fetchLoggedInUser()
        .then((userArray) => {
          if (userArray && userArray.length > 0) {
            const user = userArray[0];
            setLoggedInUser(user);
          } else {
            console.log("No logged-in user found.");
          }
        })
        .catch((error) => {
          console.error("Error fetching logged-in user:", error);
        });
    }
  }, [loggedInUser, fetchLoggedInUser, setLoggedInUser]);

  useEffect(() => {
    const fetchAndSetUserDetails = async () => {
      if (loggedInUser) {
        try {
          const clientDetails = await fetchUserDetailsById(loggedInUser);
          if (clientDetails) {
            setClientUser(clientDetails);
          } else {
            console.log("No details returned for the client.");
          }
        } catch (error) {
          console.error("Failed to fetch client details:", error);
        }
      }
    };

    fetchAndSetUserDetails();
  }, [loggedInUser, fetchUserDetailsById, setClientUser]);

    const profileImageUrl = clientUser?.profilePhoto
    ? `${imgUrl}/${clientUser.profilePhoto}`
    : `${avatarDefaultUrl}`;

  const menus = [
    "Dashboard",
    "Inbox",
    "Search workers",
    "My Jobs",
    "Manage workers",
    "Timesheets - Billing",
  ];

  const menuIcons = {
    Dashboard: (
      <MdDashboard className="icon-style" style={{ color: getRandomColor() }} />
    ),
    Inbox: (
      <TiMessages className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Search workers": (
      <FaSearch className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Manage workers": (
      <FaUserCog className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "My Jobs": (
      <FaBriefcase className="icon-style" style={{ color: getRandomColor() }} />
    ),
    "Timesheets - Billing": (
      <FaRegHourglass
        className="icon-style"
        style={{ color: getRandomColor() }}
      />
    ),
  };

  const toggleSidebar = () => {
    setShowSidebar((prevState) => {
      return !prevState;
    });
  };

  const handleAccountItemClick = (item) => {
    console.log("Clicked Account item:", item);
    setShowAccountPane((prevState) => ({ ...prevState, show: false }));
  };

  function getRandomColor() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  const renderContent = () => {
    return <Outlet context={{ clientUser }} />;
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const getMenuLinks = () => {
    return menus.map((menu) => {
      let path;
      if (menu === "Dashboard") {
        path = "/account/fs-dashboard/";
      } else if (menu === "My Jobs") {
        path = "/account/fs-dashboard/my-jobs/open";
      } else if (menu === "Timesheets - Billing") {
        path = "/account/fs-dashboard/timesheets-billing";
      } else {
        path = `/account/fs-dashboard/${menu
          .toLowerCase()
          .replace(/\s+/g, "-")}`;
      }

      return (
        <li key={menu} onClick={toggleSidebar}>
          <Link to={path} className="menu-item-link">
            {menuIcons[menu]}
            {menu}
            {menu === "Inbox" && unreadMessageCount > 0 && (
              <span className="unread-message-count">{unreadMessageCount}</span>
            )}
          </Link>
        </li>
      );
    });
  };

  return (
    <div className="dashboard-overall-wrapper">
      <>
        <AccountDashboardNavbar
          ref={navbarRef}
          toggleSidebar={toggleSidebar}
          showNotificationPane={showNotificationPane}
          setShowNotificationPane={setShowNotificationPane}
          showAccountPane={showAccountPane}
          setShowAccountPane={setShowAccountPane}
          profileImageUrl={profileImageUrl}
          notificationIconRef={notificationIconRef}
          accountIconRef={accountIconRef}
        />
        {showNotificationPane.show && (
          <div
            ref={notificationPaneRef}
            className="notifications-pane"
            style={{
              position: "fixed",
              top: isMobile ? "auto" : `${showNotificationPane.top}px`,
              left: `${showNotificationPane.left}px`,
              bottom: isMobile ? `${showNotificationPane.bottom}px` : "auto",
              transform: "translateX(-50%)",
              width: `${Math.max(250, showNotificationPane.iconWidth)}px`,
            }}
          >
            <h4>Alerts</h4>
            <ul>
              {[
                "Phone number not verified",
                "Profile photo awaiting approval.",
                "Bio awaiting approval.",
              ].map((item, index) => (
                <li key={index} onClick={() => handleAccountItemClick(item)}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {showAccountPane.show && (
          <div
            ref={accountPaneRef}
            className={`account-pane ${isMobile ? "mobile" : ""}`}
            style={{
              position: "fixed",
              top: isMobile ? "auto" : `${showAccountPane.top}px`,
              bottom: isMobile ? `${showAccountPane.bottom}px` : "auto",
              right: isMobile ? "10px" : "auto",
              left: isMobile ? "auto" : `${showAccountPane.left}px`,
              width: isMobile ? "30%" : "auto",
            }}
          >
            <h4>Account</h4>
            <ul>
              {["Profile", "Settings", "Account & Privacy", "Help"].map(
                (item, index) => (
                  <li key={index} onClick={() => handleAccountItemClick(item)}>
                    {item}
                  </li>
                )
              )}
              <li onClick={handleLogout}>Logout</li>
            </ul>
          </div>
        )}
      </>
      <div
        ref={dashboardMainContainerRef}
        className={`dashboard-main-container ${showSidebar ? "sidebar-open" : ""
          }`}
      >
        <div
          className={`sidebar ${showSidebar ? "show" : ""}`}
          ref={sidebarRef}
        >
          <div className="sidebar-profile">
            <img src={profileImageUrl} alt="Profile Thumbnail" />
            {clientUser && (
              <span>
                {clientUser.firstName} {clientUser.lastName}
              </span>
            )}
            <div className="sidebar-profile-buttons">
              <Link
                className="edit-profile-button"
                to="/account/fs-dashboard/my-account"
                onClick={toggleSidebar}
              >
                My Account
              </Link>
              <button className="logout-button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
          <ul className="sidebar-menu">{getMenuLinks()}</ul>
          {isMobile && showSidebar && (
            <div className="sidebar-close-btn" onClick={toggleSidebar}>
              X
            </div>
          )}
        </div>
        <div
          className={`dashboard-content-container ${showSidebar ? "sidebar-open" : ""
            }`}
          ref={contentRef}
        >
          {renderContent()}
        </div>
      </div>
      <WarningModal
        show={showWarning}
        remainingTime={remainingTime}
        onStayLoggedIn={resetTimer}
      />
    </div>
  );
};

export default Dashboard;
